/**
 * 问卷配置类型。
 * 注：原 types.ts 未包含在 source map 里（纯类型文件不产码），本文件按还原代码的用法重建；
 * 字段与后端 validate.mjs 的校验规则对齐，并留有索引签名以兼容未知扩展键。
 */

export type QuestionType = 'text' | 'longtext' | 'single' | 'multi' | 'slider' | 'select' | 'matrix' | 'file' | 'statement' | 'pagebreak';

/** 不参与作答、可不参与必填校验与评分的题型（标题说明 / 分页栏） */
export const NON_ANSWER_TYPES: readonly QuestionType[] = ['statement', 'pagebreak'];

export function isNonAnswerType(type: QuestionType | undefined): boolean {
  return !!type && NON_ANSWER_TYPES.includes(type);
}

export type ConditionOp = 'equals' | 'contains' | 'gt' | 'lt' | 'between';

export type Condition = { question: string; op: ConditionOp; value: any };

export type DisplayLogic = { operator: 'AND' | 'OR'; conditions: Condition[] };

/** 富文本格式：所有字段可选；颜色为 CSS 颜色字符串；字号为带单位的字符串 '16px'/'1.2em' 等 */
export type RichTextFormat = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** 文字颜色，如 '#ff0000' */
  color?: string;
  /** 字体名，如 '"Microsoft YaHei", sans-serif' */
  fontFamily?: string;
  /** 字号，如 '16px' */
  fontSize?: string;
  /** 文本对齐：'left' | 'center' | 'right' */
  align?: 'left' | 'center' | 'right';
};

/** 富文本题干：text 用于必填校验与纯文本场景（如导出/搜索），html 用于渲染 */
export type RichText = {
  text: string;
  /** 内联 HTML（含 <b>/<i>/<u>/<span style="..."> 等）；编辑时存的是渲染结果 */
  html?: string;
  /** 整体格式（适用于无内联格式的纯整体样式）；与 html 互为兜底 */
  format?: RichTextFormat;
};

/** 兼容老数据：title 可为 string 或 RichText 形态 */
export type TitleLike = string | RichText;

export function isRichText(value: unknown): value is RichText {
  return !!value && typeof value === 'object' && !Array.isArray(value) && typeof (value as { text?: unknown }).text === 'string';
}

/** 把 title 统一抽出纯文本（用于校验/必填判断/导出） */
export function titleText(value: TitleLike | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return (value as RichText).text ?? '';
}

/** 把 title 转成可渲染 HTML（字符串原样返回，富文本对象走 format + html 渲染） */
export function titleHTML(value: TitleLike | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return escapeHTML(value);
  const r = value as RichText;
  if (r.html && r.html.trim() !== '') return r.html; // 编辑器写入的 HTML
  const f = r.format ?? {};
  const text = escapeHTML(r.text ?? '');
  const openStyle: string[] = [];
  if (f.fontFamily) { openStyle.push(`font-family:${f.fontFamily}`); }
  if (f.fontSize) { openStyle.push(`font-size:${f.fontSize}`); }
  if (f.color) { openStyle.push(`color:${f.color}`); }
  const tagOpen = openStyle.length ? `<span style="${openStyle.join(';')}">` : '';
  const tagClose = openStyle.length ? `</span>` : '';
  let html = `${tagOpen}${text}${tagClose}`;
  if (f.bold) html = `<strong>${html}</strong>`;
  if (f.italic) html = `<em>${html}</em>`;
  if (f.underline) html = `<u>${html}</u>`;
  if (f.align) html = `<div style="text-align:${f.align}">${html}</div>`;
  return html;
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** 把 title 转换为 RichText（若已是 string 则包装成对象；已是 RichText 则返回原对象） */
export function ensureRichText(value: TitleLike | undefined | null): RichText {
  if (isRichText(value)) return value;
  return { text: typeof value === 'string' ? value : '', format: {} };
}

export type Option = {
  value: string;
  quota?: number;
  onQuotaFull?: 'jumpNext' | 'end';
  /** 评分题：该选项分值 */
  score?: number;
  [k: string]: any;
};

export type SliderParams = {
  min: number;
  max: number;
  /** 数值间隔（>0）；allowDecimal=false 时必须是 ≥1 的整数（0.5 倍数需 allowDecimal=true） */
  step?: number;
  unit?: string;
  /** 最小值端文本（≤24 字） */
  minLabel?: string;
  /** 最大值端文本（≤24 字） */
  maxLabel?: string;
  /** 不允许小数：true 时 step 必须是 ≥1 的整数；默认 false */
  allowDecimal?: boolean;
};

export type Question = {
  id: string;
  type: QuestionType;
  /** 主标题：可为 string（兼容老数据）或 RichText 对象 */
  title: TitleLike;
  description?: string;
  /** 描述配图（前端同学版本的题图字段；渲染时与 image 互为兜底） */
  descriptionImage?: string;
  /** 当 title 是 RichText 时是否必填（默认 true；标题说明/分页栏默认 false） */
  required: boolean;
  requiredRead?: boolean;
  /** 题干配图（data URL） */
  image?: string;
  /** 在前置页显示（随协议页一起填写，如基本信息） */
  onPreface?: boolean;
  options?: Option[];
  params?: SliderParams | { min?: number; max?: number; step?: number; unit?: string; maxLen?: number; rows?: string[]; columns?: string[]; columnScores?: (number | null)[]; allowImageUpload?: boolean; minLabel?: string; maxLabel?: string; allowDecimal?: boolean };
  displayLogic: DisplayLogic;
  [k: string]: any;
};

/** 前置页独立填写项（仅 text / longtext，随协议页一起填写） */
export type PrefaceQuestion = {
  id: string;
  type: 'text' | 'longtext';
  title: TitleLike;
  required: boolean;
  params?: { maxLen?: number; allowImageUpload?: boolean };
  [k: string]: any;
};

/** 文本题答案配图：答案为 { text, images } 对象 */
export type TextAnswer = { text: string; images: string[] };

export const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;
export const MAX_ANSWER_IMAGES = 3;

export function textAnswerText(value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value) && typeof (value as TextAnswer).text === 'string') {
    return (value as TextAnswer).text;
  }
  return typeof value === 'string' ? value : '';
}

export function textAnswerImages(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const images = (value as Partial<TextAnswer>).images;
  return Array.isArray(images) ? images.filter((image): image is string => typeof image === 'string') : [];
}

export function withTextAnswer(text: string, images: string[]): TextAnswer {
  return { text, images };
}

export type SurveySettings = {
  stopCollect?: boolean;
  anonymous?: boolean;
  voiceInput?: boolean;
  whoCanFill?: string;
  loginRequired?: boolean;
  inviteToken?: string;
  /** 每人限填一次（按浏览器设备识别） */
  oncePerUser?: boolean;
  preface?: { title?: string; body?: string; image?: string; requireCheck?: boolean; questions?: PrefaceQuestion[] };
  signature?: { enabled?: boolean; mode?: 'draw' | 'upload'; required?: boolean; onPreface?: boolean };
  feishuTable?: { appToken?: string; tableId?: string; url?: string; fieldNames?: string[] };
  editAnswer?: { enabled?: boolean; tokenTtlHours?: number };
  timeLimit?: { enabled?: boolean; start?: string; end?: string };
  quotaEnabled?: boolean;
  quota?: number;
  submitLimitEnabled?: boolean;
  submitLimit?: number;
  resultPage?: { title?: string; body?: string };
  redirectUrl?: { enabled?: boolean; url?: string };
  feishu?: { enabled?: boolean; webhookUrl?: string; bearerToken?: string; publicBaseUrl?: string };
  [k: string]: any;
};

export type SurveyConfig = {
  id: string;
  header: { mainTitle: string; subTitle?: string };
  questions: Question[];
  settings: SurveySettings;
  [k: string]: any;
};